import { Box, Heading, Text, Stack, Accordion, AccordionItem, AccordionButton, AccordionPanel, AccordionIcon, List, ListItem, useColorModeValue } from "@chakra-ui/react";

export default function TimeBoxFAQ() {
  const sectionBg = useColorModeValue("white", "gray.800");
  const pageBg = useColorModeValue("gray.50", "gray.900");
  const accent = useColorModeValue("blue.500", "blue.300");
  const borderColor = useColorModeValue("gray.200", "gray.700");

  return (
    <Box bg={pageBg} minH="100vh" py={10} px={{ base: 4, md: 8 }}>
      <Box maxW="960px" mx="auto">
        <Heading
          size="lg"
          mb={2}
          textAlign="center"
          letterSpacing="tight"
        >
          TimeBox – Frequently Asked Questions
        </Heading>
        <Text textAlign="center" color="gray.500" mb={10} fontSize="sm">
          Learn how to log, edit, and send work hours with TimeBox.
        </Text>

        <Stack spacing={8}>
          {/* General */}
          <Box
            bg={sectionBg}
            borderRadius="lg"
            borderWidth="1px"
            borderColor={borderColor}
            boxShadow="sm"
            p={{ base: 4, md: 6 }}
          >
            <Heading size="md" mb={4} color={accent}>
              General
            </Heading>
            <Accordion allowMultiple reduceMotion>
              <AccordionItem border="none">
                {({ isExpanded }) => (
                  <>
                    <AccordionButton
                      px={0}
                      py={3}
                      _expanded={{ bg: "transparent", color: accent }}
                    >
                      <Box flex="1" textAlign="left" fontWeight="semibold">
                        What is TimeBox?
                      </Box>
                      <AccordionIcon />
                    </AccordionButton>
                    <AccordionPanel pt={2} pb={4} px={0} color="gray.600" fontSize="sm">
                      TimeBox is an internal application for tracking and submitting work hours. It provides a unified
                      interface for logging time and sending it directly to Atlassian Cloud Jira and Microsoft Dynamics.
                      Developers no longer need to use multiple systems—TimeBox handles all external integrations
                      automatically.
                    </AccordionPanel>
                  </>
                )}
              </AccordionItem>

              <AccordionItem border="none">
                <AccordionButton
                  px={0}
                  py={3}
                  _expanded={{ bg: "transparent", color: accent }}
                >
                  <Box flex="1" textAlign="left" fontWeight="semibold">
                    Why do we use TimeBox?
                  </Box>
                  <AccordionIcon />
                </AccordionButton>
                <AccordionPanel pt={2} pb={1} px={0} color="gray.600" fontSize="sm">
                  TimeBox acts as the central time-reporting tool for all employees. It simplifies daily work by letting
                  developers book hours once, while project managers, finance, and management receive the data in the
                  external systems they need.
                </AccordionPanel>
              </AccordionItem>
            </Accordion>
          </Box>

          {/* Login & Access */}
          <Box
            bg={sectionBg}
            borderRadius="lg"
            borderWidth="1px"
            borderColor={borderColor}
            boxShadow="sm"
            p={{ base: 4, md: 6 }}
          >
            <Heading size="md" mb={4} color={accent}>
              Login &amp; Access
            </Heading>
            <Accordion allowMultiple reduceMotion>
              <AccordionItem borderTop="none" borderColor={borderColor}>
                <AccordionButton
                  px={0}
                  py={3}
                  _expanded={{ bg: "transparent", color: accent }}
                >
                  <Box flex="1" textAlign="left" fontWeight="semibold">
                    How can I log in to TimeBox?
                  </Box>
                  <AccordionIcon />
                </AccordionButton>
                <AccordionPanel pt={2} pb={4} px={0} color="gray.600" fontSize="sm">
                  <List spacing={2} styleType="disc" pl={4}>
                    <ListItem>
                      <strong>Atlassian Cloud Jira account</strong> – grants access to Jira boards, sprints and issues. No
                      certificates required.
                    </ListItem>
                    <ListItem>
                      <strong>Microsoft SSO</strong> – uses your Strypes/Kodar/Up2 Microsoft account; Jira boards and
                      issues are not visible with this option.
                    </ListItem>
                  </List>
                </AccordionPanel>
              </AccordionItem>

              <AccordionItem borderColor={borderColor}>
                <AccordionButton
                  px={0}
                  py={3}
                  _expanded={{ bg: "transparent", color: accent }}
                >
                  <Box flex="1" textAlign="left" fontWeight="semibold">
                    I logged in but don’t see any Jira boards. Why?
                  </Box>
                  <AccordionIcon />
                </AccordionButton>
                <AccordionPanel pt={2} pb={4} px={0} color="gray.600" fontSize="sm">
                  You are likely logged in using Microsoft SSO. Log out and sign in using your Atlassian Cloud Jira
                  credentials.
                </AccordionPanel>
              </AccordionItem>

              <AccordionItem borderColor={borderColor}>
                <AccordionButton
                  px={0}
                  py={3}
                  _expanded={{ bg: "transparent", color: accent }}
                >
                  <Box flex="1" textAlign="left" fontWeight="semibold">
                    I can’t log in with my Jira account. What should I try?
                  </Box>
                  <AccordionIcon />
                </AccordionButton>
                <AccordionPanel pt={2} pb={4} px={0} color="gray.600" fontSize="sm">
                  <List spacing={2} styleType="disc" pl={4}>
                    <ListItem>Verify you can log in to your Atlassian Cloud Jira environment.</ListItem>
                    <ListItem>If login works in Jira but not TimeBox, contact the support team.</ListItem>
                  </List>
                </AccordionPanel>
              </AccordionItem>
            </Accordion>
          </Box>

          {/* Using TimeBox */}
          <Box
            bg={sectionBg}
            borderRadius="lg"
            borderWidth="1px"
            borderColor={borderColor}
            boxShadow="sm"
            p={{ base: 4, md: 6 }}
          >
            <Heading size="md" mb={4} color={accent}>
              Using TimeBox
            </Heading>
            <Accordion allowMultiple reduceMotion>
              <AccordionItem border="none">
                <AccordionButton
                  px={0}
                  py={3}
                  _expanded={{ bg: "transparent", color: accent }}
                >
                  <Box flex="1" textAlign="left" fontWeight="semibold">
                    What can I do inside TimeBox?
                  </Box>
                  <AccordionIcon />
                </AccordionButton>
                <AccordionPanel pt={2} pb={4} px={0} color="gray.600" fontSize="sm">
                  <List spacing={2} styleType="disc" pl={4}>
                    <ListItem>Log out</ListItem>
                    <ListItem>Select Jira boards</ListItem>
                    <ListItem>View booked hours in the calendar</ListItem>
                    <ListItem>Book weekly hours via Jira issues</ListItem>
                    <ListItem>Send hours to Jira and Microsoft Dynamics</ListItem>
                    <ListItem>(Management only) Book or edit hours for colleagues</ListItem>
                    <ListItem>Customize preferences, including default board and team view</ListItem>
                  </List>
                </AccordionPanel>
              </AccordionItem>
            </Accordion>
          </Box>

          {/* Booking Hours */}
          <Box
            bg={sectionBg}
            borderRadius="lg"
            borderWidth="1px"
            borderColor={borderColor}
            boxShadow="sm"
            p={{ base: 4, md: 6 }}
          >
            <Heading size="md" mb={4} color={accent}>
              Booking Hours
            </Heading>
            <Accordion allowMultiple reduceMotion>
              <AccordionItem border="none">
                <AccordionButton
                  px={0}
                  py={3}
                  _expanded={{ bg: "transparent", color: accent }}
                >
                  <Box flex="1" textAlign="left" fontWeight="semibold">
                    How do I book hours using Jira issues?
                  </Box>
                  <AccordionIcon />
                </AccordionButton>
                <AccordionPanel pt={2} pb={4} px={0} color="gray.600" fontSize="sm">
                  Select a Jira board, choose a sprint or filter assigned issues, then click an issue to open the booking
                  window. You can book single days or a full date range.
                </AccordionPanel>
              </AccordionItem>

              <AccordionItem borderColor={borderColor}>
                <AccordionButton
                  px={0}
                  py={3}
                  _expanded={{ bg: "transparent", color: accent }}
                >
                  <Box flex="1" textAlign="left" fontWeight="semibold">
                    Can I open a Jira issue from TimeBox?
                  </Box>
                  <AccordionIcon />
                </AccordionButton>
                <AccordionPanel pt={2} pb={4} px={0} color="gray.600" fontSize="sm">
                  Yes. Right-click the issue and choose the option to open it in Jira.
                </AccordionPanel>
              </AccordionItem>

              <AccordionItem borderColor={borderColor}>
                <AccordionButton
                  px={0}
                  py={3}
                  _expanded={{ bg: "transparent", color: accent }}
                >
                  <Box flex="1" textAlign="left" fontWeight="semibold">
                    Can I book hours without accessing the Jira board?
                  </Box>
                  <AccordionIcon />
                </AccordionButton>
                <AccordionPanel pt={2} pb={4} px={0} color="gray.600" fontSize="sm">
                  You can use the "Book hours with admin ID" option. This will later be replaced with booking via Jira
                  Key.
                </AccordionPanel>
              </AccordionItem>
            </Accordion>
          </Box>

          {/* Editing Hours */}
          <Box
            bg={sectionBg}
            borderRadius="lg"
            borderWidth="1px"
            borderColor={borderColor}
            boxShadow="sm"
            p={{ base: 4, md: 6 }}
          >
            <Heading size="md" mb={4} color={accent}>
              Editing Hours
            </Heading>
            <Accordion allowMultiple reduceMotion>
              <AccordionItem border="none">
                <AccordionButton
                  px={0}
                  py={3}
                  _expanded={{ bg: "transparent", color: accent }}
                >
                  <Box flex="1" textAlign="left" fontWeight="semibold">
                    How do I change or remove booked hours?
                  </Box>
                  <AccordionIcon />
                </AccordionButton>
                <AccordionPanel pt={2} pb={4} px={0} color="gray.600" fontSize="sm">
                  Before sending hours, click any orange entry in the calendar. Modify the value or delete it by setting
                  it to 0.
                </AccordionPanel>
              </AccordionItem>

              <AccordionItem borderColor={borderColor}>
                <AccordionButton
                  px={0}
                  py={3}
                  _expanded={{ bg: "transparent", color: accent }}
                >
                  <Box flex="1" textAlign="left" fontWeight="semibold">
                    Can I edit hours after they’re sent?
                  </Box>
                  <AccordionIcon />
                </AccordionButton>
                <AccordionPanel pt={2} pb={4} px={0} color="gray.600" fontSize="sm">
                  Only management users can edit green entries after they’ve been submitted to Jira or Microsoft
                  Dynamics.
                </AccordionPanel>
              </AccordionItem>
            </Accordion>
          </Box>

          {/* Sending Hours */}
          <Box
            bg={sectionBg}
            borderRadius="lg"
            borderWidth="1px"
            borderColor={borderColor}
            boxShadow="sm"
            p={{ base: 4, md: 6 }}
          >
            <Heading size="md" mb={4} color={accent}>
              Sending Hours
            </Heading>
            <Accordion allowMultiple reduceMotion>
              <AccordionItem border="none">
                <AccordionButton
                  px={0}
                  py={3}
                  _expanded={{ bg: "transparent", color: accent }}
                >
                  <Box flex="1" textAlign="left" fontWeight="semibold">
                    What does “Send to Jira / Microsoft Dynamics” do?
                  </Box>
                  <AccordionIcon />
                </AccordionButton>
                <AccordionPanel pt={2} pb={4} px={0} color="gray.600" fontSize="sm">
                  It submits all booked hours for the week to both Atlassian Cloud Jira and Microsoft Dynamics. Submitted
                  entries turn green.
                </AccordionPanel>
              </AccordionItem>

              <AccordionItem borderColor={borderColor}>
                <AccordionButton
                  px={0}
                  py={3}
                  _expanded={{ bg: "transparent", color: accent }}
                >
                  <Box flex="1" textAlign="left" fontWeight="semibold">
                    I get the message “Resource Not configured in MSD-365.”
                  </Box>
                  <AccordionIcon />
                </AccordionButton>
                <AccordionPanel pt={2} pb={4} px={0} color="gray.600" fontSize="sm">
                  Your project isn’t fully configured in Microsoft Dynamics. Contact your Project Manager to resolve it.
                </AccordionPanel>
              </AccordionItem>
            </Accordion>
          </Box>

          {/* Booking for Others */}
          <Box
            bg={sectionBg}
            borderRadius="lg"
            borderWidth="1px"
            borderColor={borderColor}
            boxShadow="sm"
            p={{ base: 4, md: 6 }}
          >
            <Heading size="md" mb={4} color={accent}>
              Booking for Others (Management Only)
            </Heading>
            <Accordion allowMultiple reduceMotion>
              <AccordionItem border="none">
                <AccordionButton
                  px={0}
                  py={3}
                  _expanded={{ bg: "transparent", color: accent }}
                >
                  <Box flex="1" textAlign="left" fontWeight="semibold">
                    How do I book hours for another colleague?
                  </Box>
                  <AccordionIcon />
                </AccordionButton>
                <AccordionPanel pt={2} pb={4} px={0} color="gray.600" fontSize="sm">
                  Management users see a field allowing them to select another user. Enter their email, wait for data to
                  load, book hours normally, then refresh to return to your own account.
                </AccordionPanel>
              </AccordionItem>

              <AccordionItem borderColor={borderColor}>
                <AccordionButton
                  px={0}
                  py={3}
                  _expanded={{ bg: "transparent", color: accent }}
                >
                  <Box flex="1" textAlign="left" fontWeight="semibold">
                    Can I bookmark colleagues for quick access?
                  </Box>
                  <AccordionIcon />
                </AccordionButton>
                <AccordionPanel pt={2} pb={4} px={0} color="gray.600" fontSize="sm">
                  Yes. Click the star icon next to a colleague’s name to add them as a favorite. Their timesheets appear
                  as tabs.
                </AccordionPanel>
              </AccordionItem>
            </Accordion>
          </Box>

          {/* Performance Incentive */}
          <Box
            bg={sectionBg}
            borderRadius="lg"
            borderWidth="1px"
            borderColor={borderColor}
            boxShadow="sm"
            p={{ base: 4, md: 6 }}
          >
            <Heading size="md" mb={4} color={accent}>
              Performance Incentive
            </Heading>
            <Accordion allowMultiple reduceMotion>
              <AccordionItem border="none">
                <AccordionButton
                  px={0}
                  py={3}
                  _expanded={{ bg: "transparent", color: accent }}
                >
                  <Box flex="1" textAlign="left" fontWeight="semibold">
                    What is the Performance Incentive option?
                  </Box>
                  <AccordionIcon />
                </AccordionButton>
                <AccordionPanel pt={2} pb={4} px={0} color="gray.600" fontSize="sm">
                  A manager-approved tag for certain types of work. Selecting it marks hours for inclusion in the monthly
                  management report.
                </AccordionPanel>
              </AccordionItem>

              <AccordionItem borderColor={borderColor}>
                <AccordionButton
                  px={0}
                  py={3}
                  _expanded={{ bg: "transparent", color: accent }}
                >
                  <Box flex="1" textAlign="left" fontWeight="semibold">
                    Can I undo Performance Incentive hours?
                  </Box>
                  <AccordionIcon />
                </AccordionButton>
                <AccordionPanel pt={2} pb={4} px={0} color="gray.600" fontSize="sm">
                  Edit the entry and set its value to 0.
                </AccordionPanel>
              </AccordionItem>
            </Accordion>
          </Box>

          {/* Preferences & Customization */}
          <Box
            bg={sectionBg}
            borderRadius="lg"
            borderWidth="1px"
            borderColor={borderColor}
            boxShadow="sm"
            p={{ base: 4, md: 6 }}
          >
            <Heading size="md" mb={4} color={accent}>
              Preferences &amp; Customization
            </Heading>
            <Accordion allowMultiple reduceMotion>
              <AccordionItem border="none">
                <AccordionButton
                  px={0}
                  py={3}
                  _expanded={{ bg: "transparent", color: accent }}
                >
                  <Box flex="1" textAlign="left" fontWeight="semibold">
                    Where can I find Preferences?
                  </Box>
                  <AccordionIcon />
                </AccordionButton>
                <AccordionPanel pt={2} pb={4} px={0} color="gray.600" fontSize="sm">
                  Open the menu under your avatar in the top-right corner and select Preferences.
                </AccordionPanel>
              </AccordionItem>

              <AccordionItem borderColor={borderColor}>
                <AccordionButton
                  px={0}
                  py={3}
                  _expanded={{ bg: "transparent", color: accent }}
                >
                  <Box flex="1" textAlign="left" fontWeight="semibold">
                    How do I set my default Jira board?
                  </Box>
                  <AccordionIcon />
                </AccordionButton>
                <AccordionPanel pt={2} pb={4} px={0} color="gray.600" fontSize="sm">
                  Go to Preferences, locate the default board setting, choose the one you want, and save.
                </AccordionPanel>
              </AccordionItem>

              <AccordionItem borderColor={borderColor}>
                <AccordionButton
                  px={0}
                  py={3}
                  _expanded={{ bg: "transparent", color: accent }}
                >
                  <Box flex="1" textAlign="left" fontWeight="semibold">
                    Can I customize team tracking settings?
                  </Box>
                  <AccordionIcon />
                </AccordionButton>
                <AccordionPanel pt={2} pb={4} px={0} color="gray.600" fontSize="sm">
                  Management users can choose which teams to track by default under the Show Teams section.
                </AccordionPanel>
              </AccordionItem>
            </Accordion>
          </Box>

          {/* Common Issues */}
          <Box
            bg={sectionBg}
            borderRadius="lg"
            borderWidth="1px"
            borderColor={borderColor}
            boxShadow="sm"
            p={{ base: 4, md: 6 }}
          >
            <Heading size="md" mb={4} color={accent}>
              Common Issues
            </Heading>
            <Accordion allowMultiple reduceMotion>
              <AccordionItem border="none">
                <AccordionButton
                  px={0}
                  py={3}
                  _expanded={{ bg: "transparent", color: accent }}
                >
                  <Box flex="1" textAlign="left" fontWeight="semibold">
                    Not all sprints appear for my board. Why?
                  </Box>
                  <AccordionIcon />
                </AccordionButton>
                <AccordionPanel pt={2} pb={4} px={0} color="gray.600" fontSize="sm">
                  TimeBox shows all active sprints and the six most recent inactive ones. If something is missing, check
                  whether the sprint is still active in Jira.
                </AccordionPanel>
              </AccordionItem>
            </Accordion>
          </Box>
        </Stack>
      </Box>
    </Box>
  );
}